/**
 * Client helper for a workspace bulk-action endpoint (delete, set-status, …),
 * which answers either:
 *   - `200 {...}` — small selection processed inline; or
 *   - `202 {token, total, skipped}` — handed to a background job; poll the token
 *     until it completes, reporting progress along the way.
 * Side effects (polling, progress) are injected so this stays trivially testable,
 * mirroring `runCsvExport`. Domain-agnostic — first built for work items' bulk
 * delete (#2570), reused by Projects (#2594/#2595) against the same
 * `{token, status, total, processed, skipped, reason}` async-status shape;
 * each action's own sync (200) response shape is left to the caller.
 */
import type { TBulkDeleteResult, TBulkOperationStatus } from "@/types/bulk-selection";

export interface BulkOperationHandlers {
  /** Fetch the operation's current status by token. */
  poll: (token: string) => Promise<TBulkOperationStatus>;
  /** Called after each poll so the caller can surface progress (processed/total). */
  onProgress?: (status: TBulkOperationStatus) => void;
  /** Injectable sleep (tests pass an immediate resolver). */
  wait?: (ms: number) => Promise<void>;
  intervalMs?: number;
  maxAttempts?: number;
}

/** Poll `poll(token)` until the operation reaches a terminal state (completed,
 *  cancelled, or failed); throws only on failure/timeout — `cancelled` returns
 *  normally so the caller reports "N processed, then cancelled" like a partial
 *  success rather than an error. Also polls a queued Convert to Sub-task
 *  (NG-3472), whose job answers in the same shape; a failure's `reason` is the
 *  thrown error's message, which that caller shows. */
export async function pollBulkOperation(
  token: string,
  { poll, onProgress, wait, intervalMs = 1500, maxAttempts = 400 }: BulkOperationHandlers,
): Promise<TBulkOperationStatus> {
  const delay = wait ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const status = await poll(token);
    onProgress?.(status);
    if (status.status === "completed" || status.status === "cancelled") return status;
    if (status.status === "failed") throw new Error(status.reason || "Bulk action failed");
    await delay(intervalMs);
  }
  throw new Error("Bulk action timed out");
}

/** Branch a bulk-delete Response: 200 → result; 202 → poll to completion. */
export async function runBulkDelete(
  request: () => Promise<Response>,
  handlers: BulkOperationHandlers,
): Promise<TBulkDeleteResult> {
  const resp = await request();
  if (resp.status === 202) {
    const queued = (await resp.json()) as { token: string };
    const final = await pollBulkOperation(queued.token, handlers);
    return { deleted: final.processed, skipped: final.skipped };
  }
  if (!resp.ok) {
    throw new Error(`Bulk delete failed with status ${resp.status}`);
  }
  return (await resp.json()) as TBulkDeleteResult;
}

/**
 * Branch any other bulk-action Response: 200 → the action's own sync JSON
 * body (typed by the caller); 202 → poll to completion and normalize to
 * `{processed, skipped}` — an async completion carries no richer per-reason
 * breakdown than that (see `BulkOperation.reason`, which is failure-only).
 */
export async function runBulkAction<TSyncResult>(
  request: () => Promise<Response>,
  handlers: BulkOperationHandlers,
): Promise<TSyncResult | { processed: number; skipped: number }> {
  const resp = await request();
  if (resp.status === 202) {
    const queued = (await resp.json()) as { token: string };
    const final = await pollBulkOperation(queued.token, handlers);
    return { processed: final.processed, skipped: final.skipped };
  }
  if (!resp.ok) {
    throw new Error(`Bulk action failed with status ${resp.status}`);
  }
  return (await resp.json()) as TSyncResult;
}

/**
 * Whether a finished bulk action accomplished nothing at all.
 *
 * A bulk endpoint answers 200 whether it applied every item, some, or none —
 * the outcome is in the body, not the status — so "processed 0, skipped N" is
 * the shape of a *failed* action and has to be reported as one. Folding it into
 * the partial-success toast is how archiving a project-less event showed a green
 * tick while doing nothing (NG-1820).
 *
 * `cancelled` is a parameter rather than inferred, because a run the user
 * stopped is not a failure and carries its own "N processed, then cancelled".
 *
 * Lives here, beside `runBulkAction`, because it reads exactly the
 * `{processed, skipped}` shape that function returns.
 */
export function bulkActionAchievedNothing({
  processed,
  skipped,
  cancelled = false,
}: {
  processed: number;
  skipped: number;
  cancelled?: boolean;
}): boolean {
  return !cancelled && processed === 0 && skipped > 0;
}
